import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaceIcon from '@mui/icons-material/Place';
import { formatDateTime } from '../common/util/datetime';
import { humanMs } from './areas';
import { driverLabel } from './drivers';
import { request, useOperations } from './OperationsProvider';

// The map key, fetched once for the whole session rather than once per card: these cards appear
// inside map popups, and a popup that asks the server a question every time it opens is a popup
// that flickers.
// One lookup per place, kept for the session. Rounded to about ten metres, because a truck that
// has crept forward two metres is at the same address and Nominatim asks not to be asked twice.
const addresses = new Map();
const addressAt = (lat, lon) => {
  const at = `${lat.toFixed(4)},${lon.toFixed(4)}`;
  if (!addresses.has(at)) {
    const url = `https://nominatim.openstreetmap.org/reverse?${new URLSearchParams({ format: 'jsonv2', lat, lon, zoom: '17' })}`;
    addresses.set(at, fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => body?.display_name || '')
      .catch(() => ''));
  }
  return addresses.get(at);
};

let keyRequest = null;
const mapsKey = () => {
  keyRequest = keyRequest || request('/api/operations/maps').then((c) => c.googleMapsApiKey || '').catch(() => '');
  return keyRequest;
};

// What is known about the truck under the cursor, shown on the truck rather than in a corner, so
// panning keeps it attached to the vehicle. The photograph is the one taken when the vehicle was
// registered, which lets a gate operator check the screen against what is in front of them.
//
// The card itself still shows no address: it is read at a glance, and a reverse geocoder is a
// dependency and a rate limit that a glance should not wait on. Once somebody opens the map they
// have asked the question properly, and "Peenya Industrial Area, Bengaluru" answers it where
// "13.054461, 77.483863" does not.
const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const heading = (course) => (Number.isFinite(Number(course)) ? points[Math.round((Number(course) % 360) / 45) % 8] : null);
const knotsToKph = (speed) => (Number.isFinite(Number(speed)) ? `${Math.round(Number(speed) * 1.852)} km/h` : null);

// "closed 3 h 20 min ago" is read faster than a timestamp, and the timestamp is on the tooltip.
const since = (value) => {
  const at = Date.parse(value);
  if (!Number.isFinite(at)) return '';
  const age = Date.now() - at;
  return age < 60000 ? 'just now' : `${humanMs(age)} ago`;
};

const VehicleCard = ({
  vehicle, position, state, trip, onOpenTrip,
}) => {
  const navigate = useNavigate();
  // The driver register, for his number. A trip records who was driving but not how to reach him,
  // and this card is opened on a moving truck precisely in order to reach him. Read from the
  // operations context rather than taken as a prop, because the card is rendered from several
  // screens and all of them already sit inside that provider.
  const { data: operations } = useOperations();
  // The coordinates used to be a link out of the product. Somebody checking where a truck is does
  // not want to lose the yard screen to do it, so the map opens over the top and closes again.
  const [showing, setShowing] = useState(false);
  const [key, setKey] = useState(null);
  const [address, setAddress] = useState(null);
  const look = () => {
    setShowing(true);
    if (key === null) mapsKey().then(setKey);
    if (address === null && Number.isFinite(Number(position?.latitude))) {
      addressAt(Number(position.latitude), Number(position.longitude)).then(setAddress);
    }
  };
  if (!vehicle) return null;
  // A finished trip is history, not the state of the truck: its driver, challan and stage belong
  // under "last trip" at the bottom, never in the rows that describe where this vehicle is now.
  const open = trip && !trip.closedAt ? trip : null;
  const previous = trip && trip.closedAt ? trip : null;
  const rows = [
    ['Driver', driverLabel(operations?.drivers, open || vehicle)],
    ['Transporter', open?.transporter || vehicle.transporter],
    ['Challan', open?.challanNumber || open?.shipmentId],
    ['Stage', open?.stage ? open.stage.replaceAll('_', ' ').toLowerCase().replace(/^./, (s) => s.toUpperCase()) : null],
    ['Speed', knotsToKph(position?.speed)],
    ['Heading', heading(position?.course)],
    // Reported by the device rather than derived, so they are shown only when it says something.
    // Taken from the status card this product was forked from, which surfaced both.
    ['Ignition', position?.attributes?.ignition == null ? null : (position.attributes.ignition ? 'On' : 'Off')],
    ['Movement', position?.attributes?.motion == null ? null : (position.attributes.motion ? 'Moving' : 'Stopped')],
    ['Last fix', position?.fixTime ? formatDateTime(position.fixTime) : 'Never reported'],
  ].filter(([, value]) => value);
  // A device this plant may not currently watch carries no position at all, so the rows above would
  // describe a truck with no speed, no heading and no fix - which reads as a broken unit rather than
  // as somebody else's load.
  const withheld = position?.trackingStatus === 'NOT_ON_TRIP';
  const shown = withheld
    ? [['Tracking', 'While it runs your trip or stands at your site']].concat(rows.filter(([label]) => label !== 'Last fix'))
    : rows;
  const located = Number.isFinite(Number(position?.latitude)) && Number.isFinite(Number(position?.longitude));
  return (
    // Bounded so it can never be taller than the space a map popup has: the map's own container
    // clips whatever runs past its edge, and a card cut off mid-sentence looks broken rather than
    // scrollable.
    <Box sx={{
      width: 268, maxWidth: '100%', p: 0.5, maxHeight: 228, overflowY: 'auto',
    }}
    >
      <Typography variant="subtitle2" fontWeight={700} noWrap>{vehicle.name}</Typography>
      {state && <Chip size="small" sx={{ mt: 0.5 }} label={state.label} color={state.color} variant={state.color === 'default' ? 'outlined' : 'filled'} />}
      {vehicle.photoDataUrl && (
        <Box
          component="img"
          src={vehicle.photoDataUrl}
          alt={`${vehicle.name} at registration`}
          sx={{
            width: '100%', height: 104, objectFit: 'cover', borderRadius: 1, mt: 1, display: 'block',
          }}
        />
      )}
      <Stack spacing={0.25} sx={{ mt: 1 }}>
        {shown.map(([label, value]) => (
          <Stack key={label} direction="row" justifyContent="space-between" gap={1.5}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'right', minWidth: 0 }} noWrap>{value}</Typography>
          </Stack>
        ))}
      </Stack>
      {located && (
        <Link
          component="button"
          type="button"
          onClick={look}
          variant="caption"
          sx={{
            mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
          }}
        >
          {`${Number(position.latitude).toFixed(5)}, ${Number(position.longitude).toFixed(5)}`}
          <PlaceIcon sx={{ fontSize: 14 }} />
        </Link>
      )}
      {located && (
        <Dialog open={showing} onClose={() => setShowing(false)} fullWidth maxWidth="md">
          <DialogTitle sx={{ pb: 0.5 }}>{vehicle.name}</DialogTitle>
          <Box sx={{ px: 3, pb: 0.5 }}>
            <Typography variant="body2">
              {address === null ? 'Looking up the address…' : address || 'No address is recorded for this position.'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {position.fixTime ? `Last reported ${formatDateTime(position.fixTime)}` : 'No fix time recorded'}
            </Typography>
          </Box>
          <DialogContent>
            {key === null && <Typography color="text.secondary">Loading the map…</Typography>}
            {key === '' && <Typography color="text.secondary">Google Maps is not configured for this deployment, so the location can only be opened in a new tab.</Typography>}
            {!!key && (
              <Box
                component="iframe"
                title={`${vehicle.name} on the map`}
                src={`https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=${Number(position.latitude)},${Number(position.longitude)}&zoom=16`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                sx={{
                  width: '100%', height: { xs: 320, md: 460 }, border: 0, display: 'block', borderRadius: 1,
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Link
              href={`https://www.google.com/maps/search/?api=1&query=${Number(position.latitude)},${Number(position.longitude)}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mr: 'auto', ml: 1 }}
            >
              Open in Google Maps
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
            <Button onClick={() => setShowing(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      {open && <Button fullWidth size="small" sx={{ mt: 1 }} variant="outlined" onClick={() => (onOpenTrip ? onOpenTrip(open) : navigate(`/trips/${open.id}`))}>Open trip</Button>}
      {!open && previous && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            title={previous.closedAt ? formatDateTime(previous.closedAt) : undefined}
          >
            {`Parked · last trip ${previous.challanNumber || previous.shipmentId || 'unnumbered'}, closed ${since(previous.closedAt)}`}
          </Typography>
          <Button fullWidth size="small" sx={{ mt: 0.5 }} variant="outlined" onClick={() => (onOpenTrip ? onOpenTrip(previous) : navigate(`/trips/${previous.id}`))}>Open last trip</Button>
        </Box>
      )}
      {!open && !previous && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>Parked · no trip has ever been recorded for this vehicle.</Typography>}
    </Box>
  );
};

export default VehicleCard;
